import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Grid, List, Search } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useSidebar } from "../../components/ui/sidebar";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard } from "../../components/ui/cards";
import { ReportGuideIcon } from "../../components/reports/ReportGuide";
import { useNavigation } from "../../hooks/use-navigation";
import { FEATURED_REPORT, REPORT_GROUPS } from "../../constants/reportCatalog";

/** Every href the sidebar offers this user — it already applies plan + role. */
const collectHrefs = (items, out = new Set()) => {
    (items || []).forEach(item => {
        if (item?.href) out.add(item.href);
        if (item?.subItems) collectHrefs(item.subItems, out);
    });
    return out;
};

const ReportCard = ({ report, onOpen }) => {
    const Icon = report.icon;
    return (
        <div
            onClick={onOpen}
            className={cn(
                "group flex flex-col gap-2 p-4 rounded-xl border",
                "bg-card text-card-foreground border-border",
                "hover:shadow-md hover:border-primary dark:hover:border-primary",
                "cursor-pointer transition-all duration-200"
            )}
        >
            <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 dark:bg-primary text-primary group-hover:bg-primary/20 dark:group-hover:bg-primary/40 transition-colors duration-200">
                    <Icon className="w-4 h-4" />
                </div>
                <h3 className="flex-1 text-sm font-semibold text-gray-700 dark:text-foreground group-hover:text-primary dark:group-hover:text-sidebar-primary transition-colors duration-200">
                    {report.title}
                </h3>
                <ReportGuideIcon route={report.route} />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{report.description}</p>
        </div>
    );
};

const ReportRow = ({ report, onOpen }) => {
    const Icon = report.icon;
    return (
        <div
            onClick={onOpen}
            className="group flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-card border-border hover:border-primary hover:shadow-sm cursor-pointer transition-all duration-200"
        >
            <div className="p-1.5 rounded-md bg-primary/10 text-primary">
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{report.title}</h3>
                <p className="text-xs text-muted-foreground truncate">{report.description}</p>
            </div>
            <ReportGuideIcon route={report.route} />
            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
    );
};

const ReportIndex = () => {
    const navigate = useNavigate();
    const { navItems } = useNavigation();
    const { isMobile } = useSidebar();
    const [view, setView] = useState("card"); // card | list
    const [search, setSearch] = useState("");

    const allowed = useMemo(() => collectHrefs(navItems), [navItems]);
    const term = search.trim().toLowerCase();
    const matches = (r) => !term
        || r.title.toLowerCase().includes(term)
        || r.description.toLowerCase().includes(term);

    const groups = REPORT_GROUPS
        .map(g => ({ ...g, reports: g.reports.filter(r => allowed.has(r.route) && matches(r)) }))
        .filter(g => g.reports.length > 0);
    const showFeatured = allowed.has(FEATURED_REPORT.route) && matches(FEATURED_REPORT);
    const total = groups.reduce((n, g) => n + g.reports.length, 0) + (showFeatured ? 1 : 0);
    const FeaturedIcon = FEATURED_REPORT.icon;

    return (
        <ContainerCard>
            <div className="p-2 space-y-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Reports</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Grouped by when you need them — start at the top each morning. Click{" "}
                            <span className="font-medium">ⓘ</span> on any report to see what it is for.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1 max-w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="search"
                                placeholder="Search reports..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 pr-3 h-10 w-full rounded-lg bg-background border border-input placeholder:text-muted-foreground"
                            />
                        </div>
                        {!isMobile && (
                            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
                                {[["card", Grid], ["list", List]].map(([key, Icon]) => (
                                    <Button
                                        key={key}
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setView(key)}
                                        className={cn(
                                            "p-2 rounded-md transition-all duration-200",
                                            view === key
                                                ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                                                : "text-muted-foreground hover:text-foreground hover:bg-accent"
                                        )}
                                    >
                                        <Icon className="w-4 h-4" />
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {showFeatured && (
                    <div
                        onClick={() => navigate(FEATURED_REPORT.route)}
                        className="group flex items-center gap-4 p-4 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 cursor-pointer transition-colors"
                    >
                        <div className="p-3 rounded-lg bg-primary text-primary-foreground">
                            <FeaturedIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base font-semibold text-foreground">{FEATURED_REPORT.title}</h2>
                            <p className="text-sm text-muted-foreground">{FEATURED_REPORT.description}</p>
                        </div>
                        <ReportGuideIcon route={FEATURED_REPORT.route} />
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                    </div>
                )}

                {groups.map(group => (
                    <section key={group.key} className="space-y-3">
                        <div className="flex items-baseline gap-3 border-b border-border pb-2">
                            <h2 className="text-base font-semibold text-foreground">{group.title}</h2>
                            <p className="text-xs text-muted-foreground">{group.subtitle}</p>
                            <span className="ml-auto text-xs text-muted-foreground">{group.reports.length}</span>
                        </div>
                        {view === "card" ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                {group.reports.map(r => (
                                    <ReportCard key={r.route} report={r} onOpen={() => navigate(r.route)} />
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                {group.reports.map(r => (
                                    <ReportRow key={r.route} report={r} onOpen={() => navigate(r.route)} />
                                ))}
                            </div>
                        )}
                    </section>
                ))}

                {total === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="p-4 rounded-full bg-muted mb-4">
                            <Search className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">No reports found</h3>
                        <p className="text-muted-foreground max-w-md">
                            {term ? "No reports match your search. Try different keywords."
                                  : "You do not have access to any reports yet. Ask your administrator."}
                        </p>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default ReportIndex;
